import {
  legacyPermissionsControllerAtMeQueryOptions,
  useConfigControllerGetPublicTenantConfig,
} from 'korint-sdk';
import { useSetUpTotp, useUpdateMfaPreference, useVerifyTotpSetup } from 'korint-sdk/react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { Loader2Icon } from 'lucide-react';
import QRCode from 'react-qr-code';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../forms/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../forms/input-otp';
import { Center, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function TotpSetupForm({
  onSuccess,
  onBack,
}: {
  onSuccess: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { data: tenantConfig } = useConfigControllerGetPublicTenantConfig();
  const { data: user } = useQuery({ ...legacyPermissionsControllerAtMeQueryOptions() });
  const [setupDetails, setSetupDetails] = useState<{ sharedSecret: string; setupUri: URL } | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  // Guards against StrictMode's dev-mode double-effect-invocation firing a
  // second, colliding AssociateSoftwareToken call for the same in-flight setup.
  const hasStartedRef = useRef(false);

  // Resolves to undefined on failure (surfaced separately via errorMessage), never rejects.
  const { setUpTOTP } = useSetUpTotp({
    onError: err =>
      setLoadError(err instanceof Error ? err.message : t('authTotpSetup.errorGeneric')),
  });

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    const appName = tenantConfig?.displayName ?? tenantConfig?.tenantName ?? 'Korint';
    setUpTOTP().then(details => {
      if (!details) return;
      setSetupDetails({
        sharedSecret: details.sharedSecret,
        setupUri: details.getSetupUri(appName, user?.email),
      });
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount only, guarded by hasStartedRef
  }, []);

  const { verifyTOTPSetup, isLoading: isVerifying } = useVerifyTotpSetup({
    onError: err => setLoadError(mapTotpSetupError(err, t)),
  });
  const { updateMFAPreference } = useUpdateMfaPreference({
    onError: err => setLoadError(mapTotpSetupError(err, t)),
  });

  const form = useForm<{ code: string }>({
    defaultValues: { code: '' },
    resolver: zodResolver(z.object({ code: z.string().length(6) })),
  });

  const handleSubmit = form.handleSubmit(async ({ code }) => {
    setLoadError(null);
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
      onSuccess();
    } catch {}
  });

  if (loadError && !setupDetails) {
    return (
      <Stack gap={3}>
        <Alert variant="destructive">{loadError}</Alert>
        <Button className="w-full" variant="ghost" onClick={onBack}>
          {t('authMfaSetup.back')}
        </Button>
      </Stack>
    );
  }

  if (!setupDetails) {
    return (
      <Center className="py-8">
        <Loader2Icon className="w-8 h-8 animate-spin" />
      </Center>
    );
  }

  return (
    <Form {...form}>
      <Text size="sm" tone="muted" className="mb-4">
        {t('authTotpSetup.subtitle')}
      </Text>

      <Stack align="center" gap={2} className="mb-6">
        <QRCode value={setupDetails.setupUri.toString()} size={160} />
        <Text size="xs" tone="muted">
          {t('authTotpSetup.manualKeyLabel')}
        </Text>
        <code className="rounded bg-muted px-2 py-1 text-xs">{setupDetails.sharedSecret}</code>
      </Stack>

      {loadError && (
        <Alert className="mb-4" variant="destructive">
          {loadError}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem className="items-center">
              <FormControl>
                <InputOTP maxLength={6} autoFocus {...field}>
                  <InputOTPGroup>
                    {Array.from({ length: 6 }, (_, index) => (
                      <InputOTPSlot key={`totp-setup-slot-${index}`} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className="w-full"
          type="submit"
          loading={isVerifying}
          disabled={form.watch('code').length < 6}
        >
          {t('authTotpSetup.submit')}
        </Button>
        <Button className="w-full" type="button" variant="ghost" onClick={onBack}>
          {t('authMfaSetup.back')}
        </Button>
      </form>
    </Form>
  );
}

function mapTotpSetupError(err: unknown, t: TFunction): string {
  if (!(err instanceof Error)) {
    return t('authTotpSetup.errorGeneric');
  }
  const name = (err as Error & { name?: string }).name ?? '';
  if (name === 'EnableSoftwareTokenMFAException' || name === 'CodeMismatchException') {
    return t('authTotpSetup.errorInvalidCode');
  }
  if (name === 'ExpiredCodeException') return t('authTotpSetup.errorCodeExpired');
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
    return t('authTotpSetup.errorRateLimited');
  }
  return err.message || t('authTotpSetup.errorGeneric');
}
